import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import SummaryCard from './components/SummaryCard';
import FilterControls from './components/FilterControls';
import SitesTable from './components/SitesTable';
import ActivityFeed from './components/ActivityFeed';
import UrgentNotifications from './components/UrgentNotifications';
import PlanLimitBanner from './components/PlanLimitBanner';
import TaskRollupCard from './components/TaskRollupCard';
import ExpiringDocumentsCard from '../../components/ui/ExpiringDocumentsCard';
import ComplianceWatch from './components/ComplianceWatch';
import AdminOnboardingChecklist from './components/AdminOnboardingChecklist';
import CompanyOverviewPanel from './components/CompanyOverviewPanel';
import SiteReportExportModal from './components/SiteReportExportModal';
import SubscriptionStatusCard from './components/SubscriptionStatusCard';
import WorkerPerformanceTable from './components/WorkerPerformanceTable';
import TrialBanner from '../../components/TrialBanner';
import SubscriptionReadOnlyBanner from '../../components/SubscriptionReadOnlyBanner';
import { siteService } from '../../services/siteService';
import { activityService, notificationService } from '../../services/activityService';
import { companyService } from '../../services/companyService';
import { snagService } from '../../services/snagService';
import { incidentService } from '../../services/incidentService';
import { documentService } from '../../services/documentService';
import { dailyTaskService } from '../../services/taskService';
import { assignmentService } from '../../services/assignmentService';
import { workerService } from '../../services/workerService';
import { complianceService } from '../../services/complianceService';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { supabase } from '../../lib/supabase';
import Skeleton from '../../components/ui/Skeleton';




const AdminDashboard = () => {
  const navigate = useNavigate();
  const { userProfile, isDemoAdmin } = useAuth();
  const { t } = useLanguage();
  const [searchParams, setSearchParams] = useSearchParams();
  const sitesOverviewRef = useRef(null);

  // ── Stripe return detection ──────────────────────────────────────────────
  const isCheckoutSuccess = searchParams?.get('checkout') === 'success';
  const checkoutSessionId = searchParams?.get('session_id') || null;

  // Fallback state: if checkout=success but dashboard hasn't resolved in 5s
  const [checkoutFallback, setCheckoutFallback] = useState(false);
  const [checkoutFallbackError, setCheckoutFallbackError] = useState(null);
  const [checkoutSessionUser, setCheckoutSessionUser] = useState(null);
  const [showPaymentSuccessBanner, setShowPaymentSuccessBanner] = useState(false);

  // Initialize filters from URL query params
  const [filters, setFilters] = useState({
    search: searchParams?.get('search') || '',
    compliance: searchParams?.get('status') || 'all',
    sortBy: searchParams?.get('sort') || 'name',
    client: searchParams?.get('client') || ''
  });

  const [sites, setSites] = useState([]);
  const [activities, setActivities] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [company, setCompany] = useState(null);
  const [siteUsage, setSiteUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // KPI state
  const [openSnags, setOpenSnags] = useState(0);
  const [overdueTasks, setOverdueTasks] = useState(0);
  const [expiringDocuments, setExpiringDocuments] = useState(0);
  const [incidentsThisWeek, setIncidentsThisWeek] = useState(0);
  const [workersOnSite, setWorkersOnSite] = useState(0);

  // Compliance Watch state
  const [complianceAlerts, setComplianceAlerts] = useState([]);
  const [siteScores, setSiteScores] = useState([]);
  const [complianceLoading, setComplianceLoading] = useState(true);
  const [complianceRawData, setComplianceRawData] = useState({});

  // Worker Performance state — last-30-day completion rate per worker.
  // Loaded in a separate effect so it can fire as soon as company_id is
  // available, instead of waiting for the main dashboard Promise.all.
  const [workerPerformance, setWorkerPerformance] = useState([]);
  const [workerPerformanceLoading, setWorkerPerformanceLoading] = useState(true);

  // Company settings state
  const [requirePhotoEvidence, setRequirePhotoEvidence] = useState(false);
  const [photoSettingSaving, setPhotoSettingSaving] = useState(false);
  const [showSiteReportModal, setShowSiteReportModal] = useState(false);

  // ── STRIPE_RETURN_HIT — fires immediately on mount if checkout=success ──
  useEffect(() => {
    if (!isCheckoutSuccess) return;

    // Check session immediately
    const checkSession = async () => {
      try {
        const { data: { session }, error: sessionError } = await supabase?.auth?.getSession();
        if (sessionError) {
          setCheckoutFallbackError('Session error. Please try refreshing the page.');
        }
        if (session?.user) {
          setCheckoutSessionUser(session?.user);
        } else {
          setCheckoutFallbackError('No active session found. Please sign in again.');
        }

        // Company lookup
        const { data: companyData, error: companyError } = await companyService?.getUserCompany();
        if (companyError) {
          setCheckoutFallbackError('Unable to load your account. Please try refreshing.');
        } else if (companyData?.id) {
          // subscription status available — no action needed here
        } else {
          setCheckoutFallbackError('Account not found. Please try refreshing or contact support.');
        }
      } catch (err) {
        setCheckoutFallbackError('Something went wrong. Please try again.');
      }
    };

    checkSession();

    // 5-second hard fallback: if dashboard is still loading, show recovery UI
    const fallbackTimer = setTimeout(() => {
      setLoading(prev => {
        if (prev) {
          setCheckoutFallback(true);
        }
        return prev;
      });
    }, 5000);

    return () => clearTimeout(fallbackTimer);
  }, [isCheckoutSuccess]);

  // Sync filters to URL query params
  useEffect(() => {
    const params = new URLSearchParams();
    if (filters?.search) params?.set('search', filters?.search);
    if (filters?.compliance !== 'all') params?.set('status', filters?.compliance);
    if (filters?.sortBy !== 'name') params?.set('sort', filters?.sortBy);
    if (filters?.client) params?.set('client', filters?.client);

    setSearchParams(params, { replace: true });
  }, [filters, setSearchParams]);

  useEffect(() => {
    loadDashboardData();
  }, []);

  useEffect(() => {
    if (userProfile?.company_id) loadWorkerPerformance(userProfile?.company_id);
  }, [userProfile?.company_id]);

  const loadWorkerPerformance = async (companyId) => {
    setWorkerPerformanceLoading(true);
    const { data, error: err } = await workerService?.getCompletionRates(companyId, 30);
    if (err) {
      console.error('[AdminDashboard] worker performance failed:', { code: err?.code, message: err?.message });
    }
    setWorkerPerformance(data || []);
    setWorkerPerformanceLoading(false);
  };

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      
      const today = new Date()?.toISOString()?.split('T')?.[0];
      const weekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)?.toISOString()?.split('T')?.[0];
      const thirtyDaysFromNow = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)?.toISOString()?.split('T')?.[0];

      const [
        sitesResult,
        activitiesResult,
        notificationsResult,
        companyResult,
        snagsResult,
        incidentsResult,
        documentsResult,
        tasksResult,
        assignmentsResult
      ] = await Promise.all([
        siteService?.getAllSites(),
        activityService?.getAllActivities(),
        notificationService?.getAllNotifications(),
        companyService?.getUserCompany(),
        snagService?.getAllSnags(),
        incidentService?.getAllIncidents(),
        documentService?.getAllDocuments(),
        dailyTaskService?.getForDateRange(weekAgo, today, null),
        assignmentService?.getAllTodayAssignments()
      ]);

      if (sitesResult?.error) throw sitesResult.error;
      if (activitiesResult?.error) throw activitiesResult.error;
      if (notificationsResult?.error) throw notificationsResult.error;
      if (companyResult?.error) throw companyResult.error;

      setSites(sitesResult?.data || []);
      setActivities(activitiesResult?.data || []);
      setNotifications(notificationsResult?.data || []);
      setCompany(companyResult?.data);
      // Sync photo evidence setting from loaded company
      setRequirePhotoEvidence(companyResult?.data?.require_photo_evidence || false);

      // Compute open snags
      const snags = snagsResult?.data || [];
      setOpenSnags(snags?.filter(s => s?.status === 'open' || s?.status === 'in_progress')?.length);

      // Compute incidents this week
      const incidents = incidentsResult?.data || [];
      setIncidentsThisWeek(incidents?.filter(i => {
        const incidentDate = i?.incident_date || i?.created_at;
        return incidentDate && incidentDate >= weekAgo;
      })?.length);

      // Compute expiring documents (within 30 days, not expired)
      const docs = documentsResult?.data || [];
      setExpiringDocuments(docs?.filter(d => {
        if (!d?.expiry_date) return false;
        const expiry = d?.expiry_date;
        return expiry >= today && expiry <= thirtyDaysFromNow;
      })?.length);

      // Compute overdue tasks (today's tasks that are not completed and past due_time or just pending)
      const tasks = tasksResult?.data || [];
      const overdueList = dailyTaskService?.getOverdueTasks ? dailyTaskService?.getOverdueTasks(tasks) : tasks?.filter(t => t?.status !== 'completed' && t?.status !== 'blocked');
      setOverdueTasks(overdueList?.length || 0);

      // Workers on site today
      const assignments = assignmentsResult?.data || [];
      setWorkersOnSite(assignments?.length);

      // Load compliance watch data
      setComplianceLoading(true);
      const complianceResult = await complianceService?.fetchComplianceData();
      if (!complianceResult?.error && complianceResult?.data) {
        const cd = complianceResult?.data;
        const alerts = complianceService?.buildAlerts(cd);
        const scores = complianceService?.computeSiteScores({ ...cd, alerts });
        setComplianceAlerts(alerts);
        setSiteScores(scores);
        setComplianceRawData(cd);
      }
      setComplianceLoading(false);

      // Load site usage if company exists
      if (companyResult?.data?.id) {
        const usageResult = await companyService?.getActiveSiteUsage(companyResult?.data?.id);
        if (!usageResult?.error) {
          setSiteUsage(usageResult?.data);
        }
      }
    } catch (err) {
      console.error('[AdminDashboard] loadDashboardData error:', err);
      setError('Failed to load dashboard data. Please try refreshing.');
      toast?.error('Failed to load dashboard data.');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleClearFilters = () => {
    setFilters({
      search: '',
      compliance: 'all',
      sortBy: 'name',
      client: ''
    });
  };

  const hasActiveFilters = useMemo(() => {
    return filters?.search !== '' || 
           filters?.compliance !== 'all' || 
           filters?.sortBy !== 'name' || 
           filters?.client !== '';
  }, [filters]);

  const filteredSites = useMemo(() => {
    let result = [...sites];

    // Search filter (site name + client name)
    if (filters?.search) {
      const searchLower = filters?.search?.toLowerCase();
      result = result?.filter(site =>
        site?.site_name?.toLowerCase()?.includes(searchLower) ||
        site?.client_name?.toLowerCase()?.includes(searchLower)
      );
    }

    // Compliance filter
    if (filters?.compliance !== 'all') {
      result = result?.filter(site => site?.compliance_status === filters?.compliance);
    }

    // Client filter
    if (filters?.client) {
      result = result?.filter(site => site?.client_name === filters?.client);
    }

    // Sort
    switch (filters?.sortBy) {
      case 'name':
        result?.sort((a, b) => a?.name?.localeCompare(b?.name));
        break;
      case 'date':
        result?.sort((a, b) => new Date(b?.created_at) - new Date(a?.created_at));
        break;
      case 'compliance': {
        // Sort compliant→warning→critical, with NULL/unknown rows pushed to
        // the end. Without the `?? Infinity` fallback, NULL values would
        // produce NaN comparisons and unstable order. Per card 47aUEbUC —
        // brand-new sites now default to NULL compliance_status.
        const complianceOrder = { compliant: 1, warning: 2, critical: 3 };
        result?.sort((a, b) => {
          const aOrder = complianceOrder?.[a?.compliance_status] ?? Infinity;
          const bOrder = complianceOrder?.[b?.compliance_status] ?? Infinity;
          return aOrder - bOrder;
        });
        break;
      }
      default:
        break;
    }

    return result;
  }, [sites, filters]);

  const uniqueClients = useMemo(() => {
    const clients = sites?.map(site => site?.client_name)?.filter(Boolean);
    return [...new Set(clients)]?.sort();
  }, [sites]);

  const handleCardClick = (cardTitle) => {
    if (sitesOverviewRef?.current) {
      sitesOverviewRef?.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    const totalSitesLabel = t('dashCardTotalSites');
    const compliantLabel = t('dashCardCompliantSites');
    const nonCompliantLabel = t('dashCardNonCompliant');
    const openSnagsLabel = t('dashCardOpenSnags');
    const overdueTasksLabel = t('dashCardOverdueTasks');
    const expiringDocsLabel = t('dashCardExpiringDocs');
    const incidentsLabel = t('dashCardIncidents7d');
    const workersLabel = t('dashCardWorkersOnSite');

    if (cardTitle === totalSitesLabel) {
      handleClearFilters();
    } else if (cardTitle === compliantLabel) {
      setFilters(prev => ({ ...prev, compliance: 'compliant', sortBy: 'name', client: '', search: '' }));
    } else if (cardTitle === nonCompliantLabel) {
      setFilters(prev => ({ ...prev, compliance: 'critical', sortBy: 'name', client: '', search: '' }));
    } else if (cardTitle === openSnagsLabel) {
      navigate('/snags');
    } else if (cardTitle === overdueTasksLabel) {
      navigate('/task-completion-workflow');
    } else if (cardTitle === expiringDocsLabel) {
      navigate('/document-management');
    } else if (cardTitle === incidentsLabel) {
      navigate('/incident-reporting');
    } else if (cardTitle === workersLabel) {
      navigate('/workers');
    }
  };

  const summaryData = useMemo(() => {
    // okzdbyNN: strictly-active count (canonical RPC, client active-filter
    // fallback) — NOT sites.length, which counted archived rows and was
    // misread as a plan limit.
    const activeSiteCount = siteUsage?.active_count ?? (sites?.filter(s => s?.status === 'active')?.length || 0);
    const compliantSites = sites?.filter(s => s?.compliance_status === 'compliant')?.length || 0;
    const nonCompliantSites = sites?.filter(s => s?.compliance_status === 'critical' || s?.compliance_status === 'warning')?.length || 0;

    return [
      {
        title: t('dashCardTotalSites'),
        value: activeSiteCount,
        icon: 'Building2',
        color: 'var(--color-accent)',
        bgColor: 'bg-accent/10',
        clickable: true
      },
      {
        title: t('dashCardCompliantSites'),
        value: compliantSites,
        icon: 'ShieldCheck',
        color: 'var(--color-success)',
        bgColor: 'bg-success/10',
        clickable: true
      },
      {
        title: t('dashCardNonCompliant'),
        value: nonCompliantSites,
        icon: 'ShieldAlert',
        color: 'var(--color-destructive)',
        bgColor: 'bg-destructive/10',
        clickable: true
      },
      {
        title: t('dashCardOpenSnags'),
        value: openSnags,
        icon: 'AlertCircle',
        color: 'var(--color-warning)',
        bgColor: 'bg-warning/10',
        clickable: true
      },
      {
        title: t('dashCardOverdueTasks'),
        value: overdueTasks,
        icon: 'Clock',
        color: 'var(--color-destructive)',
        bgColor: 'bg-destructive/10',
        clickable: true
      },
      {
        title: t('dashCardExpiringDocs'),
        value: expiringDocuments,
        subtitle: t('dashCardWithin30Days'),
        icon: 'FileWarning',
        color: 'var(--color-warning)',
        bgColor: 'bg-warning/10',
        clickable: true
      },
      {
        title: t('dashCardIncidents7d'),
        value: incidentsThisWeek,
        subtitle: t('dashCardThisWeek'),
        icon: 'Siren',
        color: 'var(--color-destructive)',
        bgColor: 'bg-destructive/10',
        clickable: true
      },
      {
        title: t('dashCardWorkersOnSite'),
        value: workersOnSite,
        subtitle: t('dashCardToday'),
        icon: 'HardHat',
        color: 'var(--color-accent)',
        bgColor: 'bg-accent/10',
        clickable: true
      }
    ];
  }, [sites, siteUsage, openSnags, overdueTasks, expiringDocuments, incidentsThisWeek, workersOnSite, t]);

  const handleAddSite = () => {
    // Check if at limit before navigating
    if (!isDemoAdmin && siteUsage && !siteUsage?.can_add_more) {
      navigate('/subscription-management');
      return;
    }
    navigate('/site-setup-wizard');
  };

  const handleTogglePhotoEvidence = async (value) => {
    if (!company?.id) return;
    setPhotoSettingSaving(true);
    setRequirePhotoEvidence(value);
    await companyService?.updateRequirePhotoEvidence(company?.id, value);
    setPhotoSettingSaving(false);
  };

  const isAdmin = userProfile?.role === 'admin';

  // ── Checkout success toast (fires once when dashboard loads after payment) ──
  // NOTE: This useEffect MUST be before any conditional returns to avoid
  // violating React's Rules of Hooks (hooks must be called in the same order).
  useEffect(() => {
    if (isCheckoutSuccess && !loading) {
      setShowPaymentSuccessBanner(true);
      toast?.success('Payment received — your subscription is now active!');
      // okzdbyNN: do NOT re-fire loadDashboardData() here. The mount load
      // already fetched fresh state post-redirect; a second full load raced
      // the first (and SubscriptionStatusCard's own fetch) and produced the
      // 7↔5 flicker. Subscription state is refreshed on next navigation.
      // Clean checkout params from URL after a short delay so the banner stays visible
      setTimeout(() => {
        setSearchParams({}, { replace: true });
      }, 500);
    }
  }, [isCheckoutSuccess, loading]);

  // Trial lock check removed in [B9UB0M58]. Canceled / unpaid / paused
  // subscriptions now stay in-app with SubscriptionReadOnlyBanner — see
  // Routes.jsx READ_ONLY_STATUSES. The previous TrialLockScreen mount here
  // intercepted before the banner could render, leaving canceled customers
  // hard-locked after QXTUSuEM PR 1's state-machine extension. Banner now
  // owns this surface; the lock screen file is retained but not mounted.

  // ── 5-second fallback UI for post-checkout hang ──────────────────────────
  if (checkoutFallback) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6">
        <div className="bg-card border border-border rounded-xl p-8 max-w-md w-full text-center shadow-elevation-2">
          <div className="w-14 h-14 rounded-full bg-accent/10 flex items-center justify-center mx-auto mb-4">
            <Icon name="CheckCircle" size={28} className="text-accent" />
          </div>
          <h2 className="text-xl font-bold text-foreground font-heading mb-2">Payment received — finishing setup</h2>
          <p className="text-sm text-muted-foreground mb-6">
            Your payment was successful. We're waiting for your subscription to activate. This usually takes a few seconds.
          </p>
          {checkoutFallbackError && (
            <div className="bg-destructive/10 border border-destructive/30 rounded-lg p-3 mb-4 text-left">
              <p className="text-sm text-destructive">{checkoutFallbackError}</p>
            </div>
          )}
          <div className="flex flex-col gap-3">
            <button
              onClick={() => window.location?.reload()}
              className="w-full bg-accent hover:bg-accent/90 text-white font-medium py-2.5 px-4 rounded-lg transition-colors"
            >
              Reload Dashboard
            </button>
            {checkoutSessionUser && (
              <button
                onClick={() => { window.location.href = '/admin-dashboard'; }}
                className="w-full bg-card border border-border hover:bg-muted text-foreground font-medium py-2.5 px-4 rounded-lg transition-colors"
              >
                Go to Dashboard
              </button>
            )}
            <button
              onClick={() => { window.location.href = '/login'; }}
              className="w-full text-sm text-muted-foreground hover:text-foreground transition-colors py-2"
            >
              Sign in again
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="container mx-auto px-4 py-6 md:py-8 max-w-7xl pt-24">
          {/* Summary cards skeleton */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 md:gap-4 mb-8">
            {Array.from({ length: 8 })?.map((_, i) => (
              <div key={i} className="bg-card border border-border rounded-lg p-4 animate-pulse">
                <div className="h-3 bg-muted rounded w-2/3 mb-3" />
                <div className="h-7 bg-muted rounded w-1/2 mb-2" />
                <div className="h-3 bg-muted rounded w-1/3" />
              </div>
            ))}
          </div>
          {/* Sites table skeleton */}
          <div className="bg-card border border-border rounded-lg p-6 mb-6">
            <div className="h-5 bg-muted rounded w-40 mb-4 animate-pulse" />
            <div className="overflow-x-auto">
              <table className="w-full">
                <tbody>
                  <Skeleton variant="table-row" count={5} cols={5} />
                </tbody>
              </table>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="container mx-auto px-4 pt-24 pb-6 md:pb-8 max-w-7xl">
        {/* Payment success banner */}
        {showPaymentSuccessBanner && (
          <div className="mb-6 p-4 bg-success/10 border border-success/30 rounded-lg flex items-start gap-3">
            <Icon name="CheckCircle" size={20} className="text-success flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-semibold text-success mb-0.5">Payment confirmed — your subscription is now active!</p>
              <p className="text-sm text-success/80">Your new plan has been applied. All features are now available.</p>
            </div>
            <button
              onClick={() => setShowPaymentSuccessBanner(false)}
              className="text-success/60 hover:text-success transition-colors flex-shrink-0"
            >
              <Icon name="X" size={16} />
            </button>
          </div>
        )}

        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6 md:mb-8">
          <div>
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground font-heading mb-2">
              {t('adminDashboard')}
            </h1>
            <p className="text-sm md:text-base text-muted-foreground">
              {t('adminDashboardSubtitle')}
            </p>
          </div>
          <div className="flex flex-wrap gap-2 items-center">
            <Button
              variant="outline"
              size="lg"
              onClick={() => setShowSiteReportModal(true)}
              className="w-full md:w-auto"
            >
              <Icon name="FileDown" size={18} />
              Export Site Report
            </Button>
            <Button
              variant="outline"
              size="lg"
              onClick={() => navigate('/rams-management')}
              className="w-full md:w-auto"
            >
              <Icon name="FileCheck" size={18} />
              {t('dashBtnRAMS')}
            </Button>
            <Button
              variant="outline"
              size="lg"
              onClick={() => navigate('/site-inductions-management')}
              className="w-full md:w-auto"
            >
              <Icon name="GraduationCap" size={18} />
              {t('dashBtnInductions')}
            </Button>
            <Button
              variant="outline"
              size="lg"
              onClick={() => navigate('/toolbox-talks-management')}
              className="w-full md:w-auto"
            >
              <Icon name="MessageSquare" size={18} />
              {t('dashBtnToolboxTalks')}
            </Button>
            <Button
              variant="outline"
              size="lg"
              onClick={() => navigate('/site-diary-management')}
              className="w-full md:w-auto"
            >
              <Icon name="BookOpen" size={18} />
              {t('dashBtnSiteDiary')}
            </Button>
            <Button
              variant="outline"
              size="lg"
              onClick={() => navigate('/worker-certifications')}
              className="w-full md:w-auto"
            >
              <Icon name="Award" size={18} />
              {t('dashBtnCertifications')}
            </Button>
            <Button
              variant="outline"
              size="lg"
              onClick={() => navigate('/compliance-dashboard')}
              className="w-full md:w-auto"
            >
              <Icon name="ShieldCheck" size={18} />
              {t('dashBtnComplianceDashboard')}
            </Button>
            <Button
              variant="outline"
              size="lg"
              onClick={() => navigate('/checklist-management')}
              className="w-full md:w-auto"
            >
              <Icon name="ListChecks" size={18} />
              Manage Checklists
            </Button>
            <Button
              variant="outline"
              size="lg"
              onClick={() => navigate('/near-misses')}
              className="w-full md:w-auto"
            >
              <Icon name="AlertTriangle" size={18} />
              {t('dashBtnNearMisses')}
            </Button>
            <Button
              variant="outline"
              size="lg"
              onClick={() => navigate('/incident-reporting')}
              className="w-full md:w-auto"
            >
              <Icon name="ShieldAlert" size={18} />
              {t('reportIncident')}
            </Button>
            <Button
              variant="default"
              size="lg"
              onClick={handleAddSite}
              disabled={!isDemoAdmin && siteUsage && !siteUsage?.can_add_more}
              className="w-full md:w-auto"
            >
              <Icon name="Plus" size={20} />
              {t('dashBtnAddNewSite')}
            </Button>
          </div>
        </div>

        {isAdmin && !isDemoAdmin && siteUsage && (
          <PlanLimitBanner
            usage={siteUsage}
            onUpgrade={() => navigate('/subscription-management')}
          />
        )}

        {isAdmin && !isDemoAdmin && (
          <>
            <TrialBanner company={company} isDemoAdmin={isDemoAdmin} />
            <SubscriptionReadOnlyBanner className="mb-6" />
          </>
        )}

        {error && (
          <div className="bg-destructive/10 border border-destructive/30 rounded-lg p-4 mb-6">
            <div className="flex items-center gap-2 text-destructive">
              <Icon name="AlertCircle" size={20} />
              <p className="font-medium">{error}</p>
            </div>
          </div>
        )}

        <UrgentNotifications 
          notifications={notifications} 
          onViewAll={() => navigate('/notifications')} 
        />

        {isAdmin && !isDemoAdmin && (
          <AdminOnboardingChecklist />
        )}

        {isAdmin && !isDemoAdmin && (
          <SubscriptionStatusCard
            company={company}
            siteUsage={siteUsage}
            isDemoAdmin={isDemoAdmin}
          />
        )}

        {isAdmin && (
          <CompanyOverviewPanel
            sites={sites}
            openSnags={openSnags}
            expiringDocuments={expiringDocuments}
            workers={workersOnSite}
          />
        )}

        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-4 gap-3 md:gap-4 mb-6 md:mb-8">
          {summaryData?.map((card, index) => (
            <SummaryCard
              key={index}
              title={card?.title}
              value={card?.value}
              subtitle={card?.subtitle}
              icon={card?.icon}
              iconColor={card?.bgColor}
              trend={card?.trend}
              trendValue={card?.trendValue}
              onClick={card?.clickable ? () => handleCardClick(card?.title) : undefined}
            />
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8 mb-6 md:mb-8">
          <div className="lg:col-span-2" ref={sitesOverviewRef}>
            <div className="bg-card rounded-lg shadow-elevation-2 border border-border p-4 md:p-6">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
                <div>
                  <h2 className="text-lg md:text-xl font-semibold text-foreground font-heading mb-1">
                    {t('sitesOverview')}
                  </h2>
                  <p className="text-sm text-muted-foreground">
                    {filteredSites?.length} {filteredSites?.length === 1 ? t('siteFound') : t('sitesFound')}
                  </p>
                </div>
              </div>

              <FilterControls
                filters={filters}
                onFilterChange={handleFilterChange}
                onClearFilters={handleClearFilters}
                hasActiveFilters={hasActiveFilters}
                uniqueClients={uniqueClients}
              />

              <SitesTable 
                sites={filteredSites} 
                onViewSite={(siteId) => navigate(`/sites/${siteId}`)} 
              />
            </div>
          </div>

          <div className="space-y-6">
            <ActivityFeed activities={activities} />
            <TaskRollupCard />
            <ExpiringDocumentsCard maxItems={5} />
          </div>
        </div>

        {/* Worker Performance — last-30-day task completion rate per worker */}
        <div className="mb-6 md:mb-8">
          <WorkerPerformanceTable
            workers={workerPerformance}
            loading={workerPerformanceLoading}
            onWorkerClick={(workerId) => navigate(`/workers/${workerId}`)}
          />
        </div>

        {/* Compliance Watch Section */}
        <div className="mb-6 md:mb-8">
          <ComplianceWatch
            alerts={complianceAlerts}
            siteScores={siteScores}
            loading={complianceLoading}
            complianceData={{ ...complianceRawData, alerts: complianceAlerts }}
          />
        </div>

        {/* Company Settings */}
        <div className="mb-6 md:mb-8">
          <div className="bg-card rounded-lg shadow-elevation-2 border border-border p-4 md:p-6">
            <div className="flex items-center gap-2 mb-4">
              <Icon name="Settings" size={18} className="text-muted-foreground" />
              <h2 className="text-lg font-semibold text-foreground font-heading">Company Settings</h2>
            </div>
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <p className="text-sm font-medium text-foreground">Require photo evidence for task completion</p>
                <p className="text-xs text-muted-foreground mt-0.5">Workers must upload at least one photo before marking a task as complete.</p>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                {photoSettingSaving && (
                  <span className="text-xs text-muted-foreground">Saving…</span>
                )}
                <button
                  onClick={() => handleTogglePhotoEvidence(!requirePhotoEvidence)}
                  disabled={photoSettingSaving || !company?.id}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2 disabled:opacity-50 ${requirePhotoEvidence ? 'bg-accent' : 'bg-muted-foreground/30'}`}
                  role="switch"
                  aria-checked={requirePhotoEvidence}
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${requirePhotoEvidence ? 'translate-x-6' : 'translate-x-1'}`} />
                </button>
              </div>
            </div>
          </div>
        </div>
      </main>

      <SiteReportExportModal
        isOpen={showSiteReportModal}
        onClose={() => setShowSiteReportModal(false)}
        sites={sites}
      />
    </div>
  );
};

export default AdminDashboard;